const DEFAULT_CHANGE_TITLE = "Untitled change request";
const DEFAULT_CHANGE_DESCRIPTION = "Not provided";
const DEFAULT_DOCS_CHANGED = "Not specified";

function normaliseValue(value, fallback) {
  const text = typeof value === "string" ? value.trim() : "";
  return text || fallback;
}

function formatDocModules(docModules = []) {
  if (!Array.isArray(docModules) || !docModules.length) {
    return "None";
  }
  if (docModules.includes("ALL")) {
    return "All document modules";
  }
  return docModules.join(", ");
}

function buildSharedChangeRequestBlock({
  changeTitle,
  changeDescription,
  docsChanged,
}) {
  return [
    "Change request:",
    `- Title: ${normaliseValue(changeTitle, DEFAULT_CHANGE_TITLE)}`,
    `- Description: ${normaliseValue(changeDescription, DEFAULT_CHANGE_DESCRIPTION)}`,
    `- Documentation changed: ${normaliseValue(docsChanged, DEFAULT_DOCS_CHANGED)}`,
  ].join("\n");
}

function buildSharedProjectContextBlock({
  projectId,
  codeModule,
  includeDocs,
  docsMode,
  docModules,
}) {
  return [
    "Project/export context:",
    `- Project ID: ${normaliseValue(projectId, "Not provided")}`,
    `- Code module scope: ${normaliseValue(codeModule, "ALL")}`,
    `- Include docs in export: ${includeDocs ? "Yes" : "No"}`,
    `- Docs mode: ${includeDocs ? normaliseValue(docsMode, "selected") : "none"}`,
    `- Doc modules: ${includeDocs ? formatDocModules(docModules) : "None"}`,
  ].join("\n");
}

export function buildZipPlanningPrompt({
  projectId,
  codeModule,
  includeDocs,
  docsMode,
  docModules,
  changeTitle,
  changeDescription,
  docsChanged,
}) {
  return [
    "I’m attaching a DEV IDE export ZIP for a change request.",
    "",
    buildSharedChangeRequestBlock({ changeTitle, changeDescription, docsChanged }),
    "",
    buildSharedProjectContextBlock({
      projectId,
      codeModule,
      includeDocs,
      docsMode,
      docModules,
    }),
    "",
    "Do not make changes yet.",
    "",
    "Please:",
    "- review the ZIP",
    "- identify the files and existing docs that need changes",
    "- preserve the current ZIP structure",
    "- recommend the safest minimal-diff approach",
    "- list risks, assumptions, and test scenarios",
    "- confirm whether the request is viable with this ZIP alone",
  ].join("\n");
}

export function buildZipImplementationPrompt({
  projectId,
  codeModule,
  includeDocs,
  docsMode,
  docModules,
  changeTitle,
  changeDescription,
  docsChanged,
}) {
  return [
    "I’m attaching a DEV IDE export ZIP for implementation.",
    "",
    buildSharedChangeRequestBlock({ changeTitle, changeDescription, docsChanged }),
    "",
    buildSharedProjectContextBlock({
      projectId,
      codeModule,
      includeDocs,
      docsMode,
      docModules,
    }),
    "",
    "Please implement the requested change.",
    "",
    "Critical doc identity rules:",
    "- Do not create any new docs.",
    "- Update existing docs only.",
    "- Preserve the ZIP structure exactly.",
    "- Preserve all relative file paths exactly.",
    "- Preserve the exact existing doc object paths already present in the ZIP.",
    "- Preserve the exact existing metadata linkage for each doc so the importer recognizes them as changed docs, not new docs.",
    "- If a doc already exists, modify that exact doc in place.",
    "- Include the matching existing metadata file for every changed doc when required by the importer.",
    "- Do not introduce any new doc folders, renamed doc folders, duplicate doc objects, or replacement doc identities.",
    "- Before returning the ZIP, verify the package will import as changed docs, not new docs.",
    "",
    "Implementation rules:",
    "- update existing docs where needed",
    "- update existing code where needed",
    "- avoid unrelated refactors",
    "- return an import-friendly result for DEV IDE re-import",
    "- include a diff summary of the changed files",
    "- only send back changed or new files in the returned ZIP; do not include unchanged files",
    "",
    "Output requirement:",
    "- For docs, return the updated existing doc files at their exact original paths, plus any required existing metadata files needed so DEV IDE classifies them as changed docs rather than new docs.",
    "",
    "Verification checklist before returning:",
    "- changed docs > 0 when existing docs were edited",
    "- new docs = 0 unless I explicitly asked for new docs",
    "- doc folders and metadata paths match the original ZIP exactly",
    "- no duplicate doc entries exist for the same module",
    "- Documentation changed: Yes - update existing docs included in the ZIP when needed",
    "",
    "Preferred output: updated ZIP",
    "Fallback output: changed files with exact relative paths plus unified diffs",
  ].join("\n");
}

export function buildDiffReviewPrompt({
  projectId,
  changeId,
  changeTitle,
  changeDescription,
  docsChanged,
  changeSummary,
  importedFileCount,
  allDiffsText,
}) {
  return [
    "I’m attaching DEV IDE imported diffs for review.",
    "",
    buildSharedChangeRequestBlock({ changeTitle, changeDescription, docsChanged }),
    "",
    "Review context:",
    `- Project ID: ${normaliseValue(projectId, "Not provided")}`,
    `- Active change ID: ${changeId || "Not provided"}`,
    `- Import change summary: ${normaliseValue(changeSummary, DEFAULT_CHANGE_DESCRIPTION)}`,
    `- Imported code files in review: ${Number.isFinite(importedFileCount) ? importedFileCount : 0}`,
    "",
    "Please:",
    "- review the diffs",
    "- summarize what changed",
    "- identify risks or missing follow-up work",
    "- recommend the safest next minimal-diff patch",
    "- list validation and test scenarios",
    "",
    "Unified diffs:",
    allDiffsText || "(no diffs)",
  ].join("\n");
}

export function buildAgentHandoffSummary({
  phase = "Import",
  projectId,
  codeModule,
  includeDocs,
  docsMode,
  docModules,
  packageName,
  changedCodePaths = [],
  changedDocumentPaths = [],
  verificationLines = [],
  warnings = [],
  errors = [],
  recommendedNextStep = "Review the summary, then continue with the next DEV IDE workflow action.",
  codeDiffsText = "",
  documentDiffsText = "",
  includeDiffs = false,
}) {
  const safeCodePaths = Array.isArray(changedCodePaths) ? changedCodePaths.filter(Boolean) : [];
  const safeDocPaths = Array.isArray(changedDocumentPaths) ? changedDocumentPaths.filter(Boolean) : [];
  const safeVerificationLines = Array.isArray(verificationLines) && verificationLines.length
    ? verificationLines
    : ["No verification actions recorded."];
  const safeWarnings = Array.isArray(warnings) && warnings.length ? warnings : ["None"];
  const safeErrors = Array.isArray(errors) && errors.length ? errors : ["None"];

  const lines = [
    `DEV IDE Agent Handoff Summary - ${phase}`,
    "",
    "Project/package context:",
    `- Project ID: ${normaliseValue(projectId, "Not provided")}`,
    `- Code module scope: ${normaliseValue(codeModule, "ALL")}`,
    `- Package filename/path: ${normaliseValue(packageName, "Not available")}`,
    `- Docs included: ${includeDocs ? "Yes" : "No"}`,
    `- Docs mode: ${includeDocs ? normaliseValue(docsMode, "selected") : "none"}`,
    `- Doc modules: ${includeDocs ? formatDocModules(docModules) : "None"}`,
    "",
    "Changed assets:",
    `- Changed code file count: ${safeCodePaths.length}`,
    `- Changed document file count: ${safeDocPaths.length}`,
    "",
    "Changed code file paths:",
    ...(safeCodePaths.length ? safeCodePaths.map((path) => `- ${path}`) : ["- None"]),
    "",
    "Changed document file paths:",
    ...(safeDocPaths.length ? safeDocPaths.map((path) => `- ${path}`) : ["- None"]),
    "",
    "Verification status:",
    ...safeVerificationLines.map((line) => `- ${line}`),
    "",
    "Warnings:",
    ...safeWarnings.map((line) => `- ${line}`),
    "",
    "Errors:",
    ...safeErrors.map((line) => `- ${line}`),
    "",
    "Recommended next step:",
    recommendedNextStep || "Review the summary, then continue with the next DEV IDE workflow action.",
  ];

  if (includeDiffs) {
    lines.push(
      "",
      "Code diffs:",
      codeDiffsText || "(No code diffs available.)",
      "",
      "Document diffs:",
      documentDiffsText || "(No document diffs available.)",
    );
  }

  return lines.join("\n");
}
